import {
  Box, Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createActiveFilterObject } from '../../../utils';
import { useMatchListGetFilters } from '../../../hooks/useMatch';
import STRING_KEYS from '../../../language/keys';
import MultiSelect from '../../../components/MultiSelect';
import { ActiveFilters } from '../../../hooks/useTablePageParams';
import { POSITION_DETAILS_MATCH_PAGE_FILTERS } from '../../../consts';
import TableFiltersItems from '../../../components/Table/TableRenderFiltersItems';
import TableSearch from '../../../components/Table/TableSearch';
import { FilterItem } from '../../../interfaces/filterOptions.interface';
import style from './style';

type Props = {
  activeFilters: ActiveFilters,
  onChangeFilter: (filterName: string, newValue: string[]) => void,
}

const PositionDetailsFilters = ({ activeFilters, onChangeFilter }: Props): JSX.Element => {
  const { t: translate } = useTranslation();
  const {
    matchStatusesOptions, engageStatusesOptions,
  } = useMatchListGetFilters();

  const handleSearch = (searchValue: string): void => {
    onChangeFilter(POSITION_DETAILS_MATCH_PAGE_FILTERS.QUERY, [searchValue]);
  };

  const handleMatchStatusChanged = (newAccounts: string[]): void => {
    onChangeFilter(POSITION_DETAILS_MATCH_PAGE_FILTERS.MATCH_STATUS, newAccounts);
  };

  const handleEngageStatusesChanged = (newStatuses: string[]): void => {
    onChangeFilter(POSITION_DETAILS_MATCH_PAGE_FILTERS.ENGAGEMENT_STATUS, newStatuses);
  };

  const handleDelete = (filterItem: FilterItem) => {
    switch (filterItem.field) {
      case POSITION_DETAILS_MATCH_PAGE_FILTERS.QUERY: {
        handleSearch('');
        break;
      }
      case POSITION_DETAILS_MATCH_PAGE_FILTERS.MATCH_STATUS: {
        handleMatchStatusChanged(activeFilters[POSITION_DETAILS_MATCH_PAGE_FILTERS.MATCH_STATUS]
          .filter((item: string) => {
            return item !== filterItem.value;
          }));

        break;
      }
      case POSITION_DETAILS_MATCH_PAGE_FILTERS.ENGAGEMENT_STATUS: {
        handleEngageStatusesChanged(
          activeFilters[POSITION_DETAILS_MATCH_PAGE_FILTERS.ENGAGEMENT_STATUS]
            .filter((item: string) => {
              return item !== filterItem.value;
            }));

        break;
      }
      default:
        break;
    }
  };

  const getActiveFilters = (): FilterItem[] => {
    const filters: FilterItem[] = [];

    if (activeFilters[POSITION_DETAILS_MATCH_PAGE_FILTERS.QUERY]) {
      activeFilters[POSITION_DETAILS_MATCH_PAGE_FILTERS.QUERY].forEach((item: string) => {
        if (item.length > 0) {
          filters.push(
            createActiveFilterObject(
              POSITION_DETAILS_MATCH_PAGE_FILTERS.QUERY,
              translate(STRING_KEYS.FILTER_LABEL_TITLE),
              item,
            ),
          );
        }
      });
    }

    if (activeFilters[POSITION_DETAILS_MATCH_PAGE_FILTERS.MATCH_STATUS]) {
      activeFilters[POSITION_DETAILS_MATCH_PAGE_FILTERS.MATCH_STATUS].forEach((item: string) => {
        if (item.length > 0) {
          const status = matchStatusesOptions.find(({ value }) => value === item);

          filters.push(
            createActiveFilterObject(
              POSITION_DETAILS_MATCH_PAGE_FILTERS.MATCH_STATUS,
              translate(STRING_KEYS.POSITIONS_DETAILS_FILTER_MATCH_STATUS),
              item,
              status?.label,
            ),
          );
        }
      });
    }

    if (activeFilters[POSITION_DETAILS_MATCH_PAGE_FILTERS.ENGAGEMENT_STATUS]) {
      activeFilters[POSITION_DETAILS_MATCH_PAGE_FILTERS.ENGAGEMENT_STATUS]
        .forEach((item: string) => {
          if (item.length > 0) {
            const account = engageStatusesOptions.find(({ value }) => value === item);

            filters.push(
              createActiveFilterObject(
                POSITION_DETAILS_MATCH_PAGE_FILTERS.ENGAGEMENT_STATUS,
                translate(STRING_KEYS.POSITIONS_DETAILS_FILTER_ENGAGE_STATUS),
                item,
                account?.label,
              ),
            );
          }
        });
    }

    return filters;
  };

  return (
    <>
      <TableSearch
        handleSearch={ handleSearch }
      />
      <Divider />
      <Box sx={ style.main.sx }>
        <MultiSelect
          label={ translate(STRING_KEYS.POSITIONS_DETAILS_FILTER_MATCH_STATUS) }
          onChange={ handleMatchStatusChanged }
          options={ matchStatusesOptions }
          value={ activeFilters[POSITION_DETAILS_MATCH_PAGE_FILTERS.MATCH_STATUS] }
        />
        <MultiSelect
          label={ translate(STRING_KEYS.POSITIONS_DETAILS_FILTER_ENGAGE_STATUS) }
          onChange={ handleEngageStatusesChanged }
          options={ engageStatusesOptions }
          value={ activeFilters[POSITION_DETAILS_MATCH_PAGE_FILTERS.ENGAGEMENT_STATUS] }
        />
      </Box>
      <Divider />
      <TableFiltersItems filters={ getActiveFilters() } onDelete={ handleDelete } />
    </>
  );
};

export default PositionDetailsFilters;
